import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from '@/components/ui';
import { useAuthStore } from '@/stores';
import toast from 'react-hot-toast';
import { Settings, Save, Loader2, Users, Home } from 'lucide-react';
import type { ApiResponse, SiteSettings } from '@/types';

export function AdminSettings() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [siteName, setSiteName] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [homePageSlug, setHomePageSlug] = useState('');

  // Fetch settings
  const { isLoading } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: async (): Promise<SiteSettings> => {
      const response = await api.get<ApiResponse<SiteSettings>>('/admin/settings');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch settings');
      }
      const settings = response.data.data;
      setSiteName(settings.site_name);
      setSiteDescription(settings.site_description);
      setLogoUrl(settings.logo_url || '');
      setAllowRegistration(settings.allow_registration);
      setInviteOnly(settings.invite_only);
      setHomePageSlug(settings.home_page_slug || '');
      return settings;
    },
  });

  // Update settings
  const updateSettings = useMutation({
    mutationFn: async (data: Partial<SiteSettings>) => {
      const response = await api.patch<ApiResponse<SiteSettings>>('/admin/settings', data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update settings');
      }
      return response.data.data;
    },
    onSuccess: () => {
      toast.success('设置已保存');
      queryClient.invalidateQueries({ queryKey: ['adminSettings'] });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || '保存失败');
    },
  });

  const handleSave = () => {
    updateSettings.mutate({
      site_name: siteName,
      site_description: siteDescription,
      logo_url: logoUrl || null,
      allow_registration: allowRegistration,
      invite_only: inviteOnly,
      home_page_slug: homePageSlug || null,
    });
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <Settings className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">访问受限</h1>
        <p className="text-muted-foreground">您没有权限访问此页面</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-32 bg-muted rounded" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Settings className="h-6 w-6" />
        站点设置
      </h1>

      {/* Basic Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="siteName">站点名称</Label>
            <Input
              id="siteName"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Wiki Worker"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="siteDescription">站点描述</Label>
            <Input
              id="siteDescription"
              value={siteDescription}
              onChange={(e) => setSiteDescription(e.target.value)}
              placeholder="一个轻量级的 Wiki 系统"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input
              id="logoUrl"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
            />
          </div>
        </CardContent>
      </Card>

      {/* Registration Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            注册设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="allowRegistration">允许注册</Label>
              <p className="text-sm text-muted-foreground">
                关闭后新用户无法自行注册
              </p>
            </div>
            <button
              id="allowRegistration"
              onClick={() => setAllowRegistration(!allowRegistration)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                allowRegistration ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  allowRegistration ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="inviteOnly">仅限邀请</Label>
              <p className="text-sm text-muted-foreground">
                开启后只有通过邀请链接才能注册
              </p>
            </div>
            <button
              id="inviteOnly"
              onClick={() => setInviteOnly(!inviteOnly)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                inviteOnly ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  inviteOnly ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Homepage Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Home className="h-5 w-5" />
            首页设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="homePageSlug">首页页面 Slug</Label>
            <Input
              id="homePageSlug"
              value={homePageSlug}
              onChange={(e) => setHomePageSlug(e.target.value)}
              placeholder="留空则显示最近更新列表"
            />
            <p className="text-sm text-muted-foreground">
              设置特定页面作为网站首页，使用页面的 slug
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              保存中...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              保存设置
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
